/**
 * Auth Provider Settings Component
 * Main container for managing authentication providers
 */

import { Box, Typography, Alert } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthProviderSettings } from '../../../hooks/system/useAuthProviderSettings';
import type { AuthProviderSettingsProps } from './types';
import { AddProviderButton } from './components/AddProviderButton';
import { ProviderList } from './components/ProviderList';
import { ProviderConfigDialog } from './components/ProviderConfigDialog';

/**
 * Authentication Provider Settings
 * Configure and manage authentication providers for the system
 */
export const AuthProviderSettings: React.FC<AuthProviderSettingsProps> = (props) => {
  const { t } = useTranslation();

  const {
    providers,
    primaryProvider,
    loading,
    templates,
    form,
    dialogOpen,
    error,
    handleOpenAdd,
    handleOpenEdit,
    handleCloseDialog,
    handleFieldChange,
    handleProviderTypeChange,
    handleSaveProvider,
    deleteProvider,
    toggleProvider,
    setPrimaryProvider,
  } = useAuthProviderSettings(props);

  const primaryTemplate = templates[primaryProvider];
  const primaryProviderName = primaryTemplate?.name || primaryProvider;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {t('auth.providers.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('auth.providers.subtitle')}
          </Typography>
        </Box>
        <AddProviderButton onClick={handleOpenAdd} disabled={loading} />
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{t('auth.providers.primaryProvider')}:</strong> {primaryProviderName}
          <br />
          {t('auth.providers.primaryInfo')}
        </Typography>
      </Alert>

      <ProviderList
        providers={providers}
        templates={templates}
        primaryProvider={primaryProvider}
        onEdit={handleOpenEdit}
        onDelete={deleteProvider}
        onToggle={toggleProvider}
        onSetPrimary={setPrimaryProvider}
        onAdd={handleOpenAdd}
        loading={loading}
      />

      <ProviderConfigDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        mode={form.provider_type && providers.some(p => p.provider_type === form.provider_type) ? 'edit' : 'add'}
        form={form}
        onChange={handleFieldChange}
        onChangeProviderType={handleProviderTypeChange}
        onSave={handleSaveProvider}
        loading={loading}
        error={error}
        templates={templates}
      />
    </Box>
  );
};
